import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useBudgets } from '@/composables/financial/useBudgets';
import { budgetSchema, type BudgetFormData } from '@/lib/validators';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { FormDialog } from '@/components/FormDialog';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormCurrencyInput } from '@/components/forms/FormCurrencyInput';
import { FormDatePicker } from '@/components/forms/FormDatePicker';
import { formatCurrency, formatDate, formatPercentage } from '@/lib/formatters';

const currencyOptions = [
  { label: 'USD', value: 'USD' },
  { label: 'EUR', value: 'EUR' },
  { label: 'GBP', value: 'GBP' },
  { label: 'INR', value: 'INR' },
  { label: 'CAD', value: 'CAD' },
  { label: 'AUD', value: 'AUD' },
];

export function BudgetListPage() {
  const { budgets, isLoading, createBudget } = useBudgets();
  const [showCreate, setShowCreate] = useState(false);

  const form = useForm<BudgetFormData>({
    resolver: zodResolver(budgetSchema),
    defaultValues: { name: '', amount: 0, currency: 'USD', startDate: '', endDate: '', description: '', categoryId: '' },
  });

  const handleCreate = (data: BudgetFormData) => {
    createBudget.mutate(data, { onSuccess: () => { setShowCreate(false); form.reset(); } });
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <PageContainer title="Budgets" description="Track budget utilization" actions={<Button onClick={() => setShowCreate(true)}><Plus className="mr-2 h-4 w-4" />Create Budget</Button>}>
      {budgets.length === 0 ? <EmptyState title="No budgets" description="Create your first budget" actionLabel="Create Budget" onAction={() => setShowCreate(true)} /> : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {budgets.map((budget) => {
            const utilization = budget.amount > 0 ? (Number(budget.spentAmount) / Number(budget.amount)) * 100 : 0;
            const isOverBudget = utilization > 100;
            return (
              <Card key={budget.id}>
                <CardHeader><CardTitle className="text-base">{budget.name}</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm"><span>Spent</span><span className={isOverBudget ? 'text-destructive font-medium' : ''}>{formatCurrency(budget.spentAmount, budget.currency)} / {formatCurrency(budget.amount, budget.currency)}</span></div>
                  <Progress value={Math.min(utilization, 100)} className={isOverBudget ? '[&>div]:bg-destructive' : ''} />
                  <p className="text-xs text-muted-foreground">{formatPercentage(utilization)} utilized</p>
                  <p className="text-xs text-muted-foreground">{formatDate(budget.startDate)} - {formatDate(budget.endDate)}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <FormDialog open={showCreate} onOpenChange={setShowCreate} title="Create Budget" description="Set up a new budget" onSubmit={form.handleSubmit(handleCreate)} isSubmitting={createBudget.isPending}>
        <FormField form={form} name="name" label="Budget Name" required />
        <FormCurrencyInput form={form} name="amount" label="Budget Amount" />
        <FormSelect form={form} name="currency" label="Currency" options={currencyOptions} />
        <FormDatePicker form={form} name="startDate" label="Start Date" />
        <FormDatePicker form={form} name="endDate" label="End Date" />
        <FormTextarea form={form} name="description" label="Description" />
      </FormDialog>
    </PageContainer>
  );
}
